import { Container, Sprite, Text, type TextStyleOptions } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';

/** Default alias the host themes register the coin icon under.
 * Mirrors {@link BalanceDisplay}. */
const DEFAULT_COIN_ICON_ALIAS = 'shared:icon:coin';

export interface CoinAmountLabelOptions {
  /** Style passed straight to the inner {@link Text}. */
  style: TextStyleOptions;
  /** Override the coin sprite's display height. Defaults to the
   * {@code style.fontSize} (or 24 if that's missing) so the icon visually
   * matches the digit run beside it. */
  coinHeight?: number;
  /** Pixels between the digits and the coin sprite. Defaults to 6. */
  gap?: number;
  /** AssetLoader alias for the coin texture. Defaults to
   * {@link DEFAULT_COIN_ICON_ALIAS}. */
  coinIconAlias?: string;
  /** When the texture isn't loaded, fall back to plain digits (no icon).
   * Defaults to true. */
  graceful?: boolean;
}

/**
 * Compact "{number} {coin}" widget — replaces bare {@code Text} of the
 * form {@code "+123 <coin>"} so the coin glyph renders as the
 * commissioned art rather than the system coin emoji (which Apple Color
 * Emoji substitutes with a pale moon-sphere fallback).
 *
 * <p>Positions the digits at anchor (0.5, 0.5) by default; the sprite
 * sits flush right of the digits. Use {@link width} to grab the
 * combined width when laying out neighbours.
 */
export class CoinAmountLabel extends Container {
  private readonly text: Text;
  private readonly coin: Sprite | null;
  private readonly gap: number;

  constructor(initialText: string, options: CoinAmountLabelOptions) {
    super();
    this.gap = options.gap ?? 6;

    this.text = new Text({ text: initialText, style: options.style });
    this.text.anchor.set(0.5);
    this.addChild(this.text);

    const alias = options.coinIconAlias ?? DEFAULT_COIN_ICON_ALIAS;
    const texture = AssetLoader.shared.getTexture(alias);
    if (texture) {
      this.coin = new Sprite(texture);
      this.coin.anchor.set(0, 0.5);
      const targetH =
        options.coinHeight ?? this.resolveFontSize(options.style) ?? 24;
      const aspect = texture.width / texture.height;
      this.coin.height = targetH;
      this.coin.width = targetH * aspect;
      this.addChild(this.coin);
    } else {
      this.coin = null;
      if (options.graceful === false) {
        console.warn(
          `[CoinAmountLabel] coin texture '${alias}' not loaded — falling back to plain digits`,
        );
      }
    }

    this.layout();
  }

  /** Update the number string. Layout re-runs so the coin sprite stays
   * flush against the right edge of the digits. */
  setText(value: string): void {
    this.text.text = value;
    this.layout();
  }

  private layout(): void {
    if (!this.coin) {
      // No sprite — text renders centred on its own origin.
      this.text.x = 0;
      this.text.y = 0;
      return;
    }
    // Total width = textWidth + gap + coinWidth. Centre the pair on
    // (0,0): the text's right edge sits at -gap/2 - coinWidth/2, the
    // coin's left edge at +gap/2 - textWidth/2 + textWidth/2 = +gap/2.
    const halfTotal = (this.text.width + this.gap + this.coin.width) / 2;
    // Text is centre-anchored, coin is left-anchored.
    this.text.x = -halfTotal + this.text.width / 2;
    this.text.y = 0;
    this.coin.x = this.text.x + this.text.width / 2 + this.gap;
    this.coin.y = 0;
  }

  private resolveFontSize(style: TextStyleOptions): number | undefined {
    const fs = style.fontSize;
    if (typeof fs === 'number') return fs;
    if (typeof fs === 'string') {
      const n = parseFloat(fs);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }
}
